document.documentElement?.classList?.add('fc-product-js');

class ProductPurchaseForm extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.select = this.querySelector('[data-variant-select]');
    this.variantInputs = Array.from(this.querySelectorAll?.('input[name="id"]') || []);
    this.productSection = this.closest('#purchase');
    this.productScope = this.closest('main') || document;
    const productInfo = this.closest('.product-info');
    this.price = productInfo?.querySelector('[data-product-price]');
    this.comparePrice = productInfo?.querySelector('[data-compare-price]');
    this.button = this.querySelector('[data-add-button]');
    this.mobileButton = this.productSection?.querySelector('[data-mobile-add]');
    this.mobilePrice = this.productSection?.querySelector('[data-mobile-price]');
    this.mobileVariant = this.productSection?.querySelector('[data-mobile-variant-title]');
    this.statusLabel = productInfo?.querySelector('[data-product-status-label]');
    this.form = this.querySelector('form');
    this.formStatus = this.querySelector('[data-product-form-status]');
    this.announcement = this.querySelector('[data-variant-announcement]');
    this.mobileBar = this.productSection?.querySelector('[data-mobile-purchase]');
    this.skuFields = Array.from(this.productScope.querySelectorAll?.('[data-variant-sku]') || []);
    this.titleFields = Array.from(this.productScope.querySelectorAll?.('[data-variant-title]') || []);
    this.dimensionsFields = Array.from(this.productScope.querySelectorAll?.('[data-variant-dimensions]') || []);
    this.weightFields = Array.from(this.productScope.querySelectorAll?.('[data-variant-weight]') || []);
    this.dimensionRows = Array.from(this.productScope.querySelectorAll?.('[data-variant-dimensions-row]') || []);
    this.weightRows = Array.from(this.productScope.querySelectorAll?.('[data-variant-weight-row]') || []);
    this.variantSummaries = Array.from(this.productScope.querySelectorAll?.('[data-variant-summary]') || []);
    this.variantControls = this.select ? [this.select] : this.variantInputs;
    if (!this.variantControls.length) return;
    this.variantControls.forEach(control => control.addEventListener('change', () => this.updateVariantState(true), {signal:this.controller.signal}));
    this.form?.addEventListener('submit', event => this.submitProduct(event), {signal:this.controller.signal});
    this.updateVariantState(false);
    this.setupMobilePurchaseBar();
  }

  setupMobilePurchaseBar() {
    if (!this.mobileBar || !this.button || !('IntersectionObserver' in window)) return;
    this.mobileMedia = window.matchMedia('(max-width: 49.9375rem)');
    const footer = document.querySelector('footer');
    this.footerVisible = false;
    const update = () => {
      const visible = this.mobileMedia.matches && !this.button.disabled && this.button.getBoundingClientRect().bottom < 0 && !this.footerVisible;
      this.mobileBar.classList.toggle('is-visible', visible);
      this.mobileBar.toggleAttribute('aria-hidden', !visible);
      this.mobileBar.inert = !visible;
    };
    this.updateMobilePurchaseBar = update;
    this.purchaseObserver = new IntersectionObserver(() => update(), { threshold: 0 });
    this.purchaseObserver.observe(this.button);
    window.addEventListener('scroll', update, { passive: true, signal: this.controller.signal });
    window.addEventListener('resize', update, { passive: true, signal: this.controller.signal });
    this.mobileMedia.addEventListener?.('change', update, { signal: this.controller.signal });
    if (footer) {
      this.footerObserver = new IntersectionObserver(entries => {
        this.footerVisible = entries.some(entry => entry.isIntersecting);
        update();
      }, { threshold: 0 });
      this.footerObserver.observe(footer);
    }
    update();
  }

  updateVariantState(announce = true) {
    const control = this.select || this.querySelector('input[name="id"]:checked') || this.variantInputs[0];
    const option = this.select ? this.select.options[this.select.selectedIndex] : control;
    if (!option) return;
    const available = option.dataset.available === 'true';
    if (this.price) {
      this.price.textContent = option.dataset.price || 'Pricing coming soon';
      this.price.classList?.toggle('price--pending', !option.dataset.price);
    }
    if (this.comparePrice) {
      this.comparePrice.textContent = option.dataset.comparePrice || '';
      this.comparePrice.hidden = !option.dataset.comparePrice;
    }
    if (this.mobilePrice) this.mobilePrice.textContent = option.dataset.price || 'Pricing coming soon';
    if (this.mobileVariant) this.mobileVariant.textContent = (option.dataset.variantTitle || '').split(' (')[0];
    this.skuFields.forEach(field => { field.textContent = option.dataset.sku || ''; });
    this.titleFields.forEach(field => { field.textContent = option.dataset.variantTitle || ''; });
    this.dimensionsFields.forEach(field => { field.textContent = option.dataset.dimensions || ''; });
    this.weightFields.forEach(field => { field.textContent = option.dataset.weight || ''; });
    this.dimensionRows.forEach(row => { row.hidden = !option.dataset.dimensions; });
    this.weightRows.forEach(row => { row.hidden = !option.dataset.weight; });
    this.variantSummaries.forEach(summary => { summary.hidden = !(option.dataset.dimensions || option.dataset.weight); });
    const status = option.dataset.inventoryStatus || (available ? 'Ready to order' : 'Unavailable');
    if (this.statusLabel) this.statusLabel.textContent = status;
    const buttonText = available
      ? this.button?.dataset.availableText || 'Add to cart'
      : option.dataset.price
        ? this.button?.dataset.soldOutText || 'Sold out'
        : this.button?.dataset.pendingText || 'Coming soon';
    if (this.button) {
      this.button.disabled = !available;
      const label = this.button.querySelector?.('[data-add-label]') || this.button;
      label.textContent = buttonText;
    }
    if (this.mobileButton) {
      this.mobileButton.disabled = !available;
      this.mobileButton.textContent = buttonText;
    }
    this.updateMobilePurchaseBar?.();
    if (option.dataset.mediaId) {
      this.dispatchEvent(new CustomEvent('fc:variant-media', { bubbles: true, detail: { mediaId: option.dataset.mediaId } }));
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    if (announce && this.announcement) {
      this.announcement.textContent = '';
      requestAnimationFrame(() => {
        const price = option.dataset.price ? `, ${option.dataset.price}` : '';
        this.announcement.textContent = `${option.dataset.variantTitle || 'Option'} selected, ${status}${price}`;
      });
    }
  }

  async submitProduct(event) {
    if (!this.form || !window.fetch || !window.FormData) return;
    event.preventDefault();
    if (this.isSubmitting) return;

    const variant = this.form.querySelector('input[name="id"]:checked') || this.form.querySelector('input[name="id"]');
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    const quantity = Number(quantityInput?.value);
    if (!variant || variant.dataset.available !== 'true' || !Number.isInteger(quantity) || quantity < 1) {
      if (this.formStatus) this.formStatus.textContent = 'Choose an available option and a valid quantity.';
      return;
    }

    this.isSubmitting = true;
    this.form.setAttribute('aria-busy', 'true');
    this.button.disabled = true;
    if (this.mobileButton) this.mobileButton.disabled = true;
    const label = this.button.querySelector('[data-add-label]');
    if (label) label.textContent = 'Adding…';
    if (this.mobileButton) this.mobileButton.textContent = 'Adding…';
    if (this.formStatus) this.formStatus.textContent = 'Adding to your bag…';

    try {
      const addUrl = new URL(this.dataset.cartAddUrl || this.form.action, window.location.origin);
      if (!addUrl.pathname.endsWith('.js')) addUrl.pathname += '.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Please try again.');
      if (String(result.id) !== variant.value || Number(result.quantity) !== quantity) {
        throw new Error('The bag did not return the selected option and quantity. Please review it before checkout.');
      }
      if (this.formStatus) this.formStatus.textContent = 'Added to your bag. Opening your bag…';
      window.location.assign(this.dataset.cartUrl || '/cart');
    } catch (error) {
      if (this.formStatus) this.formStatus.textContent = error.message || 'Could not add this item. Please try again.';
      this.isSubmitting = false;
      this.form.removeAttribute('aria-busy');
      this.updateVariantState(false);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.purchaseObserver?.disconnect();
    this.footerObserver?.disconnect();
  }
}

if (!customElements.get('product-purchase-form')) customElements.define('product-purchase-form', ProductPurchaseForm);
